import xlsx from 'xlsx';
import dataReaderConfig from '../../../domain/constants/default-xlsx-reader-config';
import Operation from '../../../domain/dto/Operation';
import StandardOperation from '../../../domain/dto/StandardOperation';
import Debtor from '../../../domain/dto/Debtor';
import Household from '../../../domain/dto/Household';
import PaymentsHeader from '../../../domain/dto/PaymentsHeader';
import Warranty from '../../../domain/dto/Warranty';
import Payment from '../../../domain/dto/Payment';
import { setDateOfMonth, getMonthsDifferenceBetweenDates } from '../../../infrastructure/date';

// todo: llevarse los formateadores al domain y luego importarlos en los  dtos

const DATE_SEPARATOR = '/';

const formatKeys = key => key.trim().toUpperCase();

export const isValidNumber = input => {
  const inputAsString = String(input);
  // Sin separadores de miles, punto separa decimales
  const cond1 = /^(\$[ ]*)?(-\d)?\d*(\.\d+)?([ ]*%)?$/.test(inputAsString.trim());
  // Coma separa miles, punto separa decimales
  const cond2 = /^(\$[ ]*)?(-?\d{1,3}(,\d{3})*)?(\.\d+)?([ ]*%)?$/.test(inputAsString.trim());
  // signos
  const cond3 = /\$/.test(inputAsString) && /%/.test(inputAsString);
  return (cond1 || cond2) && !cond3;
};

// hipótesis: el negocio entiende que el separador decimal es el punto
export const formatNumber = input => {
  if (!isValidNumber(input)) {
    throw Error('NaN');
  }
  const inputAsString = String(input);
  if (['0', '0.0', '0.00'].includes(inputAsString)) {
    return '0';
  }
  const formattedAsString = inputAsString
    .trim()
    .replace(/^\$/, '')
    .replace(/%*/g, '')
    .replace(/,/g, '')
    .trim();
  const hasTrailingZeros = /\.\d*0+$/.test(formattedAsString);
  const formattedWithNoTrailingZeros = hasTrailingZeros ? formattedAsString.replace(/0+$/, '').replace(/\.$/, '') : formattedAsString;
  return formattedWithNoTrailingZeros.replace(/^0+/, '').replace(/^\./, '0.');
};

export const clean = value => (isValidNumber(value) ? formatNumber(value) : value.trim());

export const calculateStartOffset = (signatureDate, firstPaymentDueDate, graceMonths) => {
  const signatureDateAsDate = setDateOfMonth(signatureDate, 1);
  const firstPaymentDueDateAsDate = setDateOfMonth(firstPaymentDueDate, 1);
  const monthsDifferenceBetweenSignatureAndFirstPayment = getMonthsDifferenceBetweenDates(signatureDateAsDate, firstPaymentDueDateAsDate);
  return formatNumber(monthsDifferenceBetweenSignatureAndFirstPayment - graceMonths);
};

const sanitizeInput = input => {
  const sanitizedInput = {};
  Object.keys(input).forEach(key => {
    sanitizedInput[formatKeys(key)] = clean(input[key]);
  });
  return sanitizedInput;
};

const formatDateFromXLSFile = date => {
  if (!date) return date;
  const revert = date.substring(1, 4).includes(DATE_SEPARATOR);
  const dateToMap = revert ? date.split(DATE_SEPARATOR).reverse() : date.split(DATE_SEPARATOR);
  return dateToMap
    .map(txt => (txt.length === 1 ? `0${txt}` : txt))
    .join('-');
};
const transformStringToDate = unformattedDate => {
  return (`${unformattedDate.substring(0, 4)}-${unformattedDate.substring(4, 6)}-${unformattedDate.substring(6, 8)}`);
};

export default class GetOperationsFromXLSFile {
  constructor() {
    this.dataReader = new window.FileReader();
    this.dataReaderConfig = dataReaderConfig;
  }

  getOperationsType(workbook) {
    if (workbook.SheetNames.find(name => name === 'Operations')) return 'OPERATIONS';
    if (workbook.SheetNames.length > 0) {
      const smeSheets = ['MHE', 'T.D.'];
      const includedSheets = workbook.SheetNames.reduce((acc, value) => {
        if (smeSheets.includes(value)) {
          acc.push(value);
        }
        return acc;
      }, []);
      if (JSON.stringify(includedSheets) === JSON.stringify(smeSheets)) return 'SME';
      return 'MORTGAGE';
    }
    return undefined;
  }

  setSMEOperation(item, rowItem) {
    item.setMnemonic(rowItem.NEMOTECNICO);
    item.setIssuingCode(rowItem['CODIGO EMISOR']);
    item.setFiller1(rowItem.FILLER1);
    item.setFiller2(rowItem.FILLER2);
    item.setFiller3(rowItem.FILLER3);
    item.setAmortizationScheduleName(rowItem['NOMBRE TABLA DESARROLLO']);
    item.setEmissionRate(rowItem['TASA EMISION CONTRATO']);
    item.setContractExpirationDate(rowItem['FECHA VENCIMIENTO CONTRATO']);
    item.setAssetPurchaseRate(rowItem['TASA COMPRA ACTIVO']);
    item.setActualApprovedAmount(rowItem['VALOR NOMINAL ACTUAL']);
    item.setAssetPurchaseDate(rowItem['FECHA COMPRA ACTIVO']);
    item.setAssetPurchaseAmount(rowItem['MONTO COMPRA ACTIVO']);
    item.setAmortizedTotal(rowItem['TOTAL AMORTIZADO']);
    item.setInsuredAmount(rowItem['MONTO ASEGURADO INCENDIO']);
    item.setEvenValueOnPurchaseDate(rowItem['VALOR PAR FECHA COMPRA']);
    item.setActualValueOnPurchaseDate(rowItem['VALOR PRESENTE FECHA COMPRA']);
    item.setTemporaryAsset(rowItem['ACTIVO TRANSITORIO']);
    item.setTemporaryLiability(rowItem['PASIVO TRANSITORIO']);
    item.setContableEquity(rowItem['CAPITAL CONTABLE']);
    item.setEarnDate(rowItem['FECHA DEVENGO']);
    item.setEvenValueOnEarnDate(rowItem['VALOR PAR FECHA DEVENGO']);
    item.setShortTermEvenValue(rowItem['VALOR PAR CORTO PLAZO']);
    item.setLongTermEvenValue(rowItem['VALOR PAR LARGO PLAZO']);
    item.setEarnLiability(rowItem['PASIVO DEVENGADO']);
    item.setEarnAsset(rowItem['ACTIVO DEVENGADO']);
    item.setEarnInterest(rowItem['INTERES DEVENGADO']);
    item.setEarnAdjustment(rowItem['REAJUSTE DEVENGADO']);
    item.setAsteriskValue(rowItem['VALOR ASTERISCO']);
    item.setMarketRate(rowItem['TASA MERCADO']);
    item.setMarketIRR(rowItem['VALOR TIR MERCADO']);
    item.setDeltaIRR(rowItem['VALOR TIR DELTA']);
    item.setLastPrepaidDate(rowItem['FECHA ULTIMO PREPAGO']);
    item.setLastPrepaidAmount(rowItem['MONTO ULTIMO PREPAGO']);
    item.setLastUpdateDate(rowItem['FECHA ULTIMA ACTUALIZACION']);
    item.setNewManagerDate(rowItem['FECHA NUEVO ADMINISTRADOR']);
    item.setJudicialCollection(rowItem['FECHA COBRANZA JUDICIAL']);
    item.setLastPaymentDate(rowItem['FECHA ULTIMO PAGO']);
    // Revisar si aplica ORIGINADOR o si es lo mismo que Investor
    item.setOriginator(rowItem.ORIGINADOR);
    item.setEquity(rowItem.PATRIMONIO);
    item.setAppraiser(rowItem.EVALUADOR);
    item.setLastMonthlyPayment(rowItem['CUOTA ULTIMO PAGO']);
    item.setMarketAdjustmentValue(rowItem['VALOR AJUSTE DE MERCADO']);
    item.setDebtorIdNumberWithoutVD(rowItem['RUT DEUDOR']);
    item.setDebtorSecuritizationCompanyIdNumber(rowItem['RUT SECURITIZADORA-DEUDOR']);
    item.setNovatedIdNumber(rowItem['RUT NOVADO']);
    item.setNovatedSecuritizationCompanyIdNumber(rowItem['RUT SECURITIZADORA-NOVADO']);
    item.setManagedEquityIdNumber(rowItem['RUT PATRIMONIO EN ADMINISTRACION']);
    item.setSecuritizationCompanyIdNumber(rowItem['RUT SECURITIZADORA']);
    item.setFireInsuranceCompanyIdNumber(rowItem['RUT CIA SEGURO INCENDIO']);
    item.setFireInsuranceNumber(rowItem['POLIZA INCENDIO']);
    item.setFireInsuranceIndicator(rowItem['INDICADOR DE SEGURO INCENDIO']);
    item.setMortgageInsuranceCompanyIdNumber(rowItem['RUT CIA SEGURO DESGRAVAMEN']);
    item.setMortgageInsuranceIndicator(rowItem['INDICADOR DE SEGURO DESGRAVAMEN']);
    item.setMorgageInsuranceNumber(rowItem['POLIZA DESGRAVAMEN DEUDOR']);
    item.setTransitoryUnemploymentInsuranceCompanyIdNumber(rowItem['RUT CIA SEGURO CESANTIA/INC. TEMPORAL']);
    item.setTransitoryUnemploymentInsuranceNumber(rowItem['POLIZA CESANTIA/INCAPACIDAD TEMPORAL']);
    item.setTransitoryUnemploymentInsuranceIndicator(rowItem['INDICADOR DE SEGURO CESANTIA/INC. TEMPORAL']);
    item.setReadjustmentType(rowItem['TIPO REAJUSTE']);
    item.setPurchaseCalculationBase(rowItem['BASE CALCULO COMPRA']);
    item.setMarketCalculationBase(rowItem['BASE CALCULO MERCADO']);
    item.setActiveStatus(rowItem['ESTADO ACTIVO']);
    item.setPeriodicity(rowItem.PERIODICIDAD);
    item.setInstrumentType(rowItem['TIPO INSTRUMENTO']);
    item.setInstrumentClass(rowItem['CLASE INSTRUMENTO']);
    item.setVariableRateName(rowItem['NOMBRE TASA VARIABLE']);
    item.setRateChangePeriodicity(rowItem['PERIODICIDAD CAMBIO TASA']);
    item.setAssetValidity(rowItem['VIGENCIA ACTIVO']);
    item.setOriginator1(rowItem.ORIGINADOR_1);
    item.setLateChargesName(rowItem['NOMBRE TASA PENAL']);
    item.setLateChargesRateType(rowItem['TIPO TASA PENAL']);
    item.setVariableRateType(rowItem['TIPO TASA VARIALBLE']);
    item.setTransferralIndicator(rowItem['INDICADOR TRASPASAR']);
    item.setTransferralDate(rowItem['FECHA TRASPASO']);
    item.setNumberOfDefaults(rowItem['NUMERO VECES MOROSO']);
    item.setFiller4(rowItem.FILLER4);
    item.setFiller5(rowItem.FILLER5);
    item.setFiller6(rowItem.FILLER6);
    item.setDefaultingDays(rowItem['DIAS CON MORA']);
    item.setLastDefaultingDate(rowItem['FECHA ULTIMA MOROSIDAD']);
    item.setTimesInJudicialCollection(rowItem['VECES EN CJ']);
    item.setLastJudicialCollectionDate(rowItem['FECHA ULTIMA CJ']);
    item.setActiveStateDate(rowItem['FECHA ESTADO ACTIVO']);
    item.setGraceDays(rowItem['DIAS DE GRACIA']);
    item.setNextPaymentNumber(rowItem['PROXIMA CUOTA']);
    item.setNextPaymentDueDate(transformStringToDate(rowItem['PROXIMO VENCIMIENTO']));
    item.setPaymentDay(rowItem['DIA DE PAGO']);
    item.setManagementCommissionAmount(rowItem['MONTO COMISION ADMINISTRACION']);
    item.setInsurancesIndicator(rowItem['INDICADOR DE SEGUROS']);
    item.setPrejudicialDate(rowItem['FECHA PREJUDICIAL']);
    item.setPortfolioExpirationDate(rowItem['FECHA CARTERA VENCIDA']);
    item.setMonthlyPayment(rowItem['VALOR DIVIDENDO']);
    item.setPortfolioPunishmentDate(rowItem['FECHA CARTERA CASTIGADA']);
    item.setVariableRate(rowItem['VALOR TASA VARIABLE']);
    item.setVariableRateChangeDate(rowItem['FECHA CAMBIO TASA VARIABLE']);
    item.setNumberOfCoupons(rowItem['NUMERO DE CUPONES']);
    item.setAdjustmentDate(rowItem['FECHA AJUSTE']);
    item.setSaleDate(rowItem['FECHA VENTA']);
    item.setCalculatesLateChargesInterest(rowItem['CALCULA INTERES PENAL']);
    item.setLateChargesInterestRate(rowItem['TASA INTERES PENAL']);
    item.setDebtorNovationDate(rowItem['FECHA NOVACION DEUDOR']);
    item.setAssetType(rowItem['TIPO ACTIVO']);
    item.setFiller7(rowItem.FILLER7);
    item.setChargeObservations(rowItem['OBSERVACIONES A LA CARGA']);
    item.setActualBalance(rowItem['SALDO REAL']);
    item.setLastPaymentDueDate(rowItem['VENCIMIENTO ULTIMA CUOTA PAGADA']);
    item.setActualPurchaseRate(rowItem['TASA EFECTIVA COMPRA']);
    item.setReadjustmentDate(rowItem['FECHA REAJUSTE']);
    item.setSubsidyValue(rowItem['MONTO SUBSIDIO']);
    item.setSubsidyType(rowItem['Tipo de Subsidio']);
  }

  setPayments(item, rowItem) {
    item.setPaymentNumber(rowItem['NUMERO CUOTA']);
    item.setAmortization(rowItem.AMORTIZACION);
    item.setInterest(rowItem.INTERES);
    item.setMonthlyPayment(rowItem['TOTAL CUOTA']);
    item.setBalance(rowItem['SALDO DEUDA']);
    item.setIsPaid(false);
  }

  setDebtor(item, rowItem) {
    item.setPrincipalIdNumberWithoutVD(rowItem['RUT MANDANTE']);
    item.setIdNumberWithoutVD(rowItem['RUT DEUDOR']);
    item.setIdNumberVD(rowItem.DV);
    item.setFathersLastName(rowItem['APELLIDO PATERNO']);
    item.setMothersLastName(rowItem['APELLIDO MATERNO']);
    item.setIncomesCurrency(rowItem['MONEDA INGRESOS']);
    item.setName(rowItem.NOMBRES);
    item.setBirthdate(rowItem['FECHA NACIMIENTO']);
    item.setActivityCode(rowItem['CODIGO ACTIVIDAD']);
    item.setBankAccountNumber(rowItem['Nº CUENTA CORRIENTE']);
    item.setIsDependent(rowItem.INDEPENDIENTE);
    item.setPropertySeparationRegime(rowItem['SEPARACION DE BIENES']);
    item.setNumberOfLiabilities(rowItem['Nº CARGAS']);
    item.setAddress(rowItem.DIRECCION);
    item.setPostalCode(rowItem['CODIGO POSTAL']);
    item.setParticularPhoneNumber(rowItem['TELEFONO PARTICULAR']);
    item.setCommercialPhoneNumber(rowItem['TELEFONO COMERCIAL']);
    item.setCommercialAnnex(rowItem['ANEXO COMERCIAL']);
    item.setCommercialFax(rowItem['FAX COMERCIAL']);
    item.setFinancialDebt(rowItem['DEUDA VIGENTE SIST. FINANCIERO']);
    item.setFinancialDefaultingDebt(rowItem['DEUDA MOROSA SIST. FINANCIERO']);
    item.setOverdueFinancialDebt(rowItem['DEUDA VENCIDA SIST. FINANCIERO']);
    item.setNumberOfProtests(rowItem['Nº PROTESTOS']);
    item.setCommercialDebt(rowItem['DEUDA COMERCIAL']);
    item.setLastEmploymentEntranceDate(rowItem['FECHA INGRESO ULT. EMPLEO']);
    item.setCellphoneNumber(rowItem.CELULAR);
    item.setJobTitle(rowItem['CARGO EMPRESA']);
    item.setMonthlyIncome(rowItem['INGRESO MENSUAL ']);
    item.setEmployerIdNumber(rowItem['RUT EMPLEADOR']);
    item.setEmployerName(rowItem['NOMBRE EMPLEADOR']);
    item.setEmployerAddress(rowItem['DIRECCION EMPLEADOR']);
    item.setEmployerPhoneNumber(rowItem['TELEFONO EMPLEADOR']);
    item.setEmployerAnnex(rowItem['ANEXO EMPLEADOR']);
    item.setClearedUpProtests(rowItem['PROTESTOS ACLARADOS']);
    item.setNonCLearedUpProtests(rowItem['PROTESTOS NO ACLARADOS']);
    item.setSicomDefaulting(rowItem['MOROSIDAD SICOM']);
    item.setCountry(rowItem.PAIS);
    item.setNationality(rowItem.NACIONALIDAD);
    item.setEmployerCountry(rowItem['PAIS EMPLEADOR']);
    item.setEducation(rowItem['TIPO EDUCACION']);
    item.setSex(rowItem.SEXO);
    item.setCivilStatus(rowItem['ESTADO CIVIL']);
    item.setInstitution(rowItem.INSTITUCION);
    item.setEmployerCommune(rowItem['COMUNA EMPLEADOR']);
    item.setEmail(rowItem['CORREO CONTACTO']);
    item.setDebtorCommune(rowItem['COMUNA DEUDOR']);
    item.setProfession(rowItem.PROFESION);
    item.setEmploymentStatus(rowItem['SITUACION LABORAL']);
    item.setValidity(rowItem.VIGENCIA);
  }

  setHousehold(item, rowItem) {
    item.setHouseholdId(rowItem['ROL VIVIENDA']);
    item.setCurrency(rowItem.MONEDA);
    item.setCountry(rowItem.PAIS);
    item.setAppraiserIdNumber(rowItem['RUT TASADOR']);
    item.setClientType(rowItem['TIPO CLIENTE']);
    item.setHousingComplexName(rowItem['NOMBRE CONJUNTO']);
    item.setSignatureDate(rowItem['FECHA ESCRITURA']);
    item.setProjectName(rowItem['NOMBRE PROYECTO']);
    item.setAddress(rowItem.DIRECCION);
    item.setBlock(rowItem.BLOQUE);
    item.setApartmentNumber(rowItem['NUMERO DPTO']);
    item.setVilla(rowItem.VILLA);
    item.setPostalCode(rowItem['CODIGO POSTAL']);
    item.setBuiltDate(rowItem['FECHA CONSTRUCCION']);
    item.setBuiltSqMts(rowItem['M2 CONSTRUIDOS']);
    item.setLandSqMts(rowItem['M2 TERRENO']);
    item.setBuildingApartmentNumber(rowItem['NUMERO DPTO. EDIFICIO']);
    item.setNumberOfBedrooms(rowItem['NUMERO DORMITORIOS']);
    item.setNumberOfBathrooms(rowItem['NUMERO BAÑOS']);
    item.setRealStateConservatorName(rowItem['NOMBRE CBR']);
    item.setRealStateConservatorInscriptionDate(rowItem['FECHA INS. CBR']);
    item.setNotaryIdNumberWithoutVD(rowItem['RUT NOTARIO']);
    item.setNotaryIdNumberVD(rowItem['DV NOTARIO']);
    item.setDomainSheetNumber(rowItem['FOJAS DOMINIO']);
    item.setDomainNumber(rowItem['NUMERO DOMINIO']);
    item.setDomainYear(rowItem['AÑO DOMINIO']);
    item.setInscriptionNumber(rowItem['NUM. INSCRIPCION']);
    item.setAppraisalValue(rowItem['VALOR TASACION']);
    item.setAppraisalDate(formatDateFromXLSFile(rowItem['FECHA TASACION']));
    item.setMunicipalReceptionCertificateNumber(rowItem['NUMERO CERT. RECEP. MUNICIPAL']);
    item.setMunicipalReceptionDate(rowItem['FECHA RECEPCION MUNICIPAL']);
    item.setTaxAppraisal(rowItem['AVALUO FISCAL']);
    item.setLiquidationValue(rowItem['VALOR LIQUIDACION']);
    item.setDescription(rowItem.DESCRIPCION);
    item.setOwnerIdNumberWithoutVD(rowItem['RUT PROPIETARIO']);
    item.setOwnerIdNumberVD(rowItem['DV PROPIETARIO']);
    item.setOwnerName(rowItem['NOMBRE PROPIETARIO']);
    item.setOwnerLivesAtHousehold(rowItem['PROPIETARIO VIVE EN VIVIENDA']);
    item.setHouseholdCommune(rowItem['COMUNA VIVIENDA']);
    item.setPropertyType(rowItem['TIPO PROPIEDAD']);
    item.setHouseholdType(rowItem['TIPO VIVIENDA']);
    item.setHouseholdDestination(rowItem['DESTINO VIVIENDA']);
    item.setValidity(rowItem.VIGENCIA);
    item.setPropertyStatus(rowItem['ESTADO PROPIEDAD']);
    item.setConstructionType(rowItem['TIPO CONSTRUCCION']);
    item.setHouseholdStatus(rowItem['ESTADO VIVIENDA']);
    item.setPurchaseValue(rowItem['VALOR COMPRAVENTA']);
    item.setLandValue(rowItem['VALOR TERRENO']);
    item.setAssociationStatus(rowItem['ESTADO ASOCIACION']);
    item.setDownPaymentValue(rowItem['VALOR PIE']);
  }

  setWarranty(item, rowItem) {
    item.setPrincipalIdNumberWithoutVD(rowItem['RUT MANDANTE']);
    item.setIdNumberWithoutVD(rowItem['RUT DEUDOR']);
    item.setAssignmentDate(rowItem['FECHA ASIGNACION']);
    item.setGuarantorIdNumberWithoutVD(rowItem['RUT GARANTE']);
    item.setSecuritizerIdNumberWithoutVD(rowItem['RUT SECURITIZADORA']);
    item.setAndesCompanyOperationId(rowItem['Nº OPERACIÓN']);
    item.setCodebtorType(rowItem['TIPO DE CODEUDOR']);
  }

  setPaymentsHeader(item, rowItem) {
    item.setAndesCompanyOperationId(rowItem['NOMBRE TABLA DESARROLLO']);
    item.setEmissionRate(rowItem['TASA EMISION']);
    item.setCouponValue(rowItem['VALOR CUPON']);
    item.setNumberOfCoupons(rowItem['NUMERO CUPONES']);
    item.setInterestType(rowItem['TIPO INTERES']);
    item.setGraceCoupons(rowItem['CUPONES GRACIA']);
    item.setCalculationBase(rowItem['BASE CALCULO']);
  }

  getSheetRows(sheetName, rows, sheetLookupId, isArray = false) {
    const sheetRows = rows.reduce((acc, row) => {
      const rowItem = sanitizeInput(row);
      const { item, lookupColumn, setMethod } = {
        MHEData: { item: new Operation(), lookupColumn: 'NEMOTECNICO_1', setMethod: this.setSMEOperation },
        TDData: { item: new Payment(), lookupColumn: 'NEMOTECNICO', setMethod: this.setPayments },
        deudoresData: { item: new Debtor(), lookupColumn: '__EMPTY', setMethod: this.setDebtor },
        viviendasData: { item: new Household(), lookupColumn: 'RUT PROPIETARIO', setMethod: this.setHousehold },
        garantiasData: { item: new Warranty(), lookupColumn: 'Nº OPERACIÓN', setMethod: this.setWarranty },
        cabeceraTDData: { item: new PaymentsHeader(), lookupColumn: 'NOMBRE TABLA DESARROLLO', setMethod: this.setPaymentsHeader }
      }[sheetName];
      if (sheetLookupId === rowItem[lookupColumn]) {
        setMethod(item, rowItem);
        acc.push(item);
      }
      return acc;
    }, []);
    if (!isArray) {
      if (sheetRows.length === 0) {
        throw Error(`no element detected in operation for sheet ${sheetName}`);
      }
      if (sheetRows.length > 1) {
        throw Error(`multiple element detected in operation for sheet ${sheetName}`);
      }
      return sheetRows.shift();
    }
    return sheetRows;
  }

  processSMEOperations(workbook) {
    const MHEData = xlsx.utils.sheet_to_json(workbook.Sheets.MHE, { raw: this.dataReaderConfig.RAW });
    const deudoresData = xlsx.utils.sheet_to_json(workbook.Sheets.DEUDORES, { raw: this.dataReaderConfig.RAW });
    const viviendasData = xlsx.utils.sheet_to_json(workbook.Sheets.VIVIENDAS, { raw: this.dataReaderConfig.RAW });
    const garantiasData = xlsx.utils.sheet_to_json(workbook.Sheets.GARANTIAS, { raw: this.dataReaderConfig.RAW });
    const cabeceraTDData = xlsx.utils.sheet_to_json(workbook.Sheets['CABECERA TD'], { raw: this.dataReaderConfig.RAW });
    const TDData = xlsx.utils.sheet_to_json(workbook.Sheets['T.D.'], { raw: this.dataReaderConfig.RAW });

    return MHEData.reduce((ops, operationIn) => {
      const item = new Operation();
      const operation = sanitizeInput(operationIn);
      const andesCompanyOperationId = operation[formatKeys('Nº OPERACIÓN')];
      item.setAndesCompanyOperationId(andesCompanyOperationId);
      const companyOperationId = operation[formatKeys('NEMOTECNICO_1')];
      if (['OBLIGATORIO', 'ALFANUMÉRICO 15'].includes(andesCompanyOperationId)) {
        return ops;
      }

      const approvedAmount = operation[formatKeys('VALOR NOMINAL ACTUAL')];
      const signatureDate = transformStringToDate(operation[formatKeys('FECHA EMISION CONTRATO')]);
      const annualRate = operation[formatKeys('TASA EMISION CONTRATO')];
      const termAsMonths = String(Number(operation[formatKeys('PLAZO CREDITO')]));
      const graceMonths = String(Number(operation[formatKeys('PROXIMA CUOTA')], 10) - 1); // existe la llave grace months en la hoja payments header

      const operationData = this.getSheetRows('MHEData', MHEData, companyOperationId);
      const debtor = this.getSheetRows('deudoresData', deudoresData, companyOperationId);
      const household = this.getSheetRows('viviendasData', viviendasData, debtor.idNumberWithoutVD);
      const warranty = this.getSheetRows('garantiasData', garantiasData, andesCompanyOperationId);
      const paymentsHeader = this.getSheetRows('cabeceraTDData', cabeceraTDData, andesCompanyOperationId);

      // necesarios para el dto de la integración
      item.setCompanyOperationId(companyOperationId);
      item.setAprovedAmount(approvedAmount);
      item.setSignatureDate(signatureDate);
      item.setAnnualRate(annualRate);
      item.setType('SME');
      item.setTermAsMonths(termAsMonths);
      item.setGraceMonths(graceMonths);
      item.setInvestorName('CREDITU');
      item.setFirstDueDateMonthsToAdd(calculateStartOffset(signatureDate, operationData.nextPaymentDueDate, graceMonths));
      item.setDueDay(operationData.paymentDay);
      item.setPeriodicity(operationData.periodicity);
      item.setCodebtorIdNumber('');
      item.setDebtorIdNumber(`${debtor.idNumberWithoutVD}-${debtor.idNumberVD}`);
      item.setDebtorEmail(debtor.email);
      item.setDebtorName(debtor.name);
      item.setDebtorLastName('');
      // TODO: setAmortizationMethod cuando distingamos con claridad tipos de amortización en créditos desestructurados

      const payments = this.getSheetRows('TDData', TDData, andesCompanyOperationId, true);
      item.setPayments(payments);

      item.setFieldsFromRegisterProcess({ operationData, debtor, household, warranty, paymentsHeader });

      ops.push(item);
      return ops;
    }, []);
  }

  processMortgageOperations(workbook, country) {
    const sheetName = workbook.SheetNames[0];
    const workbookData = xlsx.utils.sheet_to_json(workbook.Sheets[sheetName], { raw: this.dataReaderConfig.RAW });
    return workbookData.map(operationIn => {
      const item = new Operation();
      const operation = sanitizeInput(operationIn);

      if (country === 'cl') {
        item.setType('MORTGAGE');
        item.setSubTypeOperation(operation[formatKeys('Segmento')]);
        item.setOrigin();
        item.setOperationNumber(operation[formatKeys('N° de operación')]);
        item.setCompanyOperationId(operation[formatKeys('Nro Operación SVS')]);
        // 06-12-2022: Guillermo y Demian indicaron que la fecha de firma debía ser la más antigua
        // item.setSignatureDate(formatDateFromXLSFile(operation[formatKeys('Fecha firma deudor')]));
        item.setSignatureDate(formatDateFromXLSFile(operation[formatKeys('Fecha primera firma')]));
        item.setAnnualRate(operation[formatKeys('Tasa de emisión')]);
        item.setEmissionRate(operation[formatKeys('Tasa de emisión')]);
        item.setEAC(operation[formatKeys('CAE')]);
        item.setTermAsMonths(operation[formatKeys('Plazo de crédito')], 'cl');
        item.setGraceMonths(operation[formatKeys('Meses de gracia')]);
        item.setPropertyAmount(operation[formatKeys('Precio venta')]);
        item.setAprovedAmount(operation[formatKeys('Total crédito')]);
        item.setUninsuredCreditAmount(operation[formatKeys('Monto crédito sin póliza')]);
        item.setPaymentValue(operation[formatKeys('Valor cuota')]);
        item.setDebtorLifeInsuranceNumber(operation[formatKeys('N° de póliza desgravamen deudor')]);
        item.setCodebtorLifeInsuranceNumber(operation[formatKeys('N° de póliza desgravamen codeudor')]);
        item.setSegment(operation[formatKeys('Segmento')]);
        item.setSubsidyType(operation[formatKeys('Tipo de Subsidio')]);
        item.setSubsidyValue(operation[formatKeys('Valor subsidio')]);

        item.setPropertyAddress(operation[formatKeys('Dirección propiedad')]);
        item.setPropertyUnit(operation[formatKeys('Unidad de la propiedad')]);
        item.setCommune(operation[formatKeys('Comuna')]);
        item.setCity(operation[formatKeys('Ciudad')]);
        item.setProjectName(operation[formatKeys('Proyecto')]);
        item.setPropertyType(operation[formatKeys('Tipo de propiedad')]);
        item.setPropertyAppraisal(operation[formatKeys('Valor tasación departamento')]);
        item.setTerrainAppraisal(operation[formatKeys('Valor tasación terreno')]);
        item.setCellarAppraisal(operation[formatKeys('Valor tasación bodega')]);
        item.setParkingLotAppraisal(operation[formatKeys('Valor tasación estacionamiento')]);
        item.setAppraisalTotal(operation[formatKeys('Valor tasación total')]);
        item.setAppraisalDate(formatDateFromXLSFile(operation[formatKeys('Fecha tasación')]));

        item.setInsurablePropertyValue(operation[formatKeys('Valor asegurable departamento')]);
        item.setInsurableTerrainValue(operation[formatKeys('Valor asegurable terreno')]);
        item.setInsurableCellarValue(operation[formatKeys('Valor asegurable bodega')]);
        item.setInsurableParkingLotValue(operation[formatKeys('Valor asegurable estacionamiento')]);
        item.setInsurableValueTotal(operation[formatKeys('Valor asegurable total')]);
        item.setFrontEndRatioTotal(operation[formatKeys('Dividendo renta total')]);
        item.setBackendRatioTotal(operation[formatKeys('Carga financiera total')]);
        item.setLoanToValuePercentage(operation[formatKeys('Porcentaje LTV')]);
        item.setInsuredLoanToValuePercentage(operation[formatKeys('Porcentaje LTV total con póliza')]);

        item.setNotary(operation[formatKeys('Notaria')]);
        item.setRealStateConservator(operation[formatKeys('CBR')]);
        item.setRealStateConservatorRegistrationSheet(operation[formatKeys('Fojas (Inscripcion CBR)')]);
        item.setRealStateConservatorRegistrationNumber(operation[formatKeys('Numero (Inscripcion CBR)')]);
        item.setRealStateConservatorRegistrationYear(operation[formatKeys('Año (Inscripcion CBR)')]);
        item.setRealStateConservatorExitSignatureDate(formatDateFromXLSFile(operation[formatKeys('Fecha firma salida CBR')]));
        item.setStampEndorsementDate(formatDateFromXLSFile(operation[formatKeys('Fecha de timbre endoso')]));
        item.setRole(operation[formatKeys('ROL')]);

        item.setDebtorDPS(operation[formatKeys('DPS deudor')]);
        item.setCodebtorDPS(operation[formatKeys('DPS Codeudor')]);
        item.setDebtorType(operation[formatKeys('Tipo deudor')]);
        item.setDebtorName(operation[formatKeys('Nombre deudor')]);
        item.setDebtorLastName(operation[formatKeys('Primer apellido deudor')]);
        item.setDebtorSecondLastName(operation[formatKeys('Segundo apellido deudor')]);
        item.setDebtorIdNumber(operation[formatKeys('Rut deudor')]);
        item.setDebtorBirthdate(formatDateFromXLSFile(operation[formatKeys('Fecha de nacimiento deudor')]));
        item.setDebtorGender(operation[formatKeys('Genero deudor')]);
        item.setDebtorMaritalStatus(operation[formatKeys('Estado civil deudor')]);
        item.setDebtorConjugalRegime(operation[formatKeys('Regimen conyugal deudor')]);
        item.setDebtorWorkerType(operation[formatKeys('TIPO TRABAJADOR deudor')]);
        item.setDebtorNationality(operation[formatKeys('NACIONALIDAD deudor')]);
        item.setDebtorProfession(operation[formatKeys('PROFESION deudor')]);
        item.setDebtorAddress(operation[formatKeys('Dirección deudor')]);
        item.setDebtorAddressCommune(operation[formatKeys('Comuna de dirección deudor')]);
        item.setDebtorPhone(operation[formatKeys('Teléfono deudor')]);
        item.setDebtorEmail(operation[formatKeys('Correo electrónico deudor')]);
        item.setDebtorMonthlySalary(operation[formatKeys('Sueldo mensual deudor')]);

        item.setCodebtorName(operation[formatKeys('Nombre codeudor')]);
        item.setCodebtorLastName(operation[formatKeys('Primer apellido codeudor')]);
        item.setCodebtorSecondLastName(operation[formatKeys('Segundo apellido codeudor')]);
        item.setCodebtorIdNumber(operation[formatKeys('Rut codeudor')]);
        item.setCodebtorBirthdate(formatDateFromXLSFile(operation[formatKeys('Fecha de nacimiento codeudor')]));
        item.setCodebtorGender(operation[formatKeys('Genero codeudor')]);
        item.setCodebtorAddress(operation[formatKeys('Dirección codeudor')]);
        item.setCodebtorAddressCommune(operation[formatKeys('Comuna de dirección codeudor')]);
        item.setCodebtorAddressPhone(operation[formatKeys('Teléfono codeudor')]);
        item.setCodebtorEmail(operation[formatKeys('Correo electrónico codeudor')]);
        item.setCodebtorMonthlySalary(operation[formatKeys('Sueldo mensual codeudor')]);
        item.setCodebtorWorkerType(operation[formatKeys('tipo trabajador codeudor')]);
        item.setCodebtorMaritalStatus(operation[formatKeys('Estado civil codeudor')]);
        item.setCodebtorConjugalRegime(operation[formatKeys('Regimen conyugal codeudor')]);
        item.setCodebtorNationality(operation[formatKeys('Nacionalidad Codeudor')]);
        item.setCodebtorProfession(operation[formatKeys('Profesión codeudor')]);

        item.setInvestorPaymentDate(formatDateFromXLSFile(operation[formatKeys('Fecha pago Inversionista')]));
        item.setInvestorName(operation[formatKeys('Nombre Inversionista')]);

        item.setFirstSignatureDate(formatDateFromXLSFile(operation[formatKeys('Fecha primera firma')]));
        item.setRepertoireNumber(operation[formatKeys('N.º Repertorio')]);

        // Mientras usemos sólo este tipo de estructuras en Chile para personas
        item.setAmortizationMethod('FRENCH');
        item.setDueDay('10');
        item.setFirstDueDateMonthsToAdd(2);
      }

      if (country === 'br') {
        item.setType(operation[formatKeys('type')]);
        item.setIsActive(operation[formatKeys('isActive')]);
        item.setCompanyOperationId(operation[formatKeys('companyOperationId')]);
        item.setAprovedAmount(operation[formatKeys('approvedAmount')]);
        item.setTermAsMonths(operation[formatKeys('termAsMonths')]);
        item.setGraceMonths(operation[formatKeys('graceMonths')]);
        item.setFirstPayment(formatDateFromXLSFile(operation[formatKeys('firstPayment')]));
        item.setPropertyInsuranceType(operation[formatKeys('propertyInsuranceType')]);
        item.setDebtorLifeInsuranceType(operation[formatKeys('debtorLifeInsuranceType')]);
        item.setSignatureDate(formatDateFromXLSFile(operation[formatKeys('signatureDate')]));
        item.setAnnualRate(operation[formatKeys('annualRate')]);
        item.setAmortizationMethod(operation[formatKeys('amortizationMethod')]);
        item.setPropertyAmount(operation[formatKeys('propertyAmount')]);
        item.setInflationaryUpperBound(operation[formatKeys('inflationaryUpperBound')]);
        item.setInvestorName(operation[formatKeys('investorName')]);
        item.setDueDay(operation[formatKeys('dueDay')]);
        item.setCodebtorLifeInsuranceType(operation[formatKeys('codebtorLifeInsuranceType')]);
        item.setCodebtorLifeInsuranceProportion(operation[formatKeys('codebtorLifeInsuranceProportion')]);
        item.setCodebtorBirthdate(formatDateFromXLSFile(operation[formatKeys('codebtorBirthdate')]));
        item.setUpperBoundBehaviour(operation[formatKeys('upperBoundBehaviour')]);

        item.setDebtorEmail(operation[formatKeys('email')]);
        item.setDebtorName(operation[formatKeys('name')]);
        item.setDebtorLastName(operation[formatKeys('lastName')]);
        item.setDebtorIdNumber(operation[formatKeys('idNumber')]);
        item.setDebtorPhone(operation[formatKeys('phone')]);
        item.setDebtorAddress(operation[formatKeys('address')]);
        item.setDebtorAddressCEP(operation[formatKeys('cep')]);
        item.setDebtorBirthdate(formatDateFromXLSFile(operation[formatKeys('birthdate')]));

        item.setCreditInsuranceAmount(operation[formatKeys('creditInsuranceAmount')]);
        item.setMonthlyCreditInsurance(operation[formatKeys('monthlyCreditInsurance')]);
        item.setCreditInsuranceRate(operation[formatKeys('creditInsuranceRate')]);
        item.setCreditInsurancePrizeProportion(operation[formatKeys('creditInsurancePrizeProportion')]);
        item.setCreditInsuranceOuterProportion(operation[formatKeys('creditInsuranceOuterProportion')]);
        item.setCreditInsuranceNumberOfMonths(operation[formatKeys('creditInsuranceNumberOfMonths')]);

        item.setConsiderTAC(operation[formatKeys('considerTAC')]);
        item.setConsiderNegativeInflation(operation[formatKeys('considerNegativeInflation')]);
      }

      return item;
    });
  }

  fixStringBoolean(objArrayIn) {
    const trueValues = [true, 'true', 'TRUE', 'verdadero', 'VERDADERO'];
    const falseValues = [false, 'false', 'FALSE', 'falso', 'FALSO'];
    const objArray = objArrayIn;
    const fixedObjArray = objArray.map(objIn => {
      const obj = objIn;
      Object.entries(obj).forEach(([key, val]) => {
        if (trueValues.includes(val)) obj[key] = 'TRUE';
        else if (falseValues.includes(val)) obj[key] = 'FALSE';
      });
      return obj;
    });
    return fixedObjArray;
  }

  fixString(objArrayIn) {
    const objArray = objArrayIn;
    const fixedObjArray = objArray.map(objIn => {
      const obj = objIn;
      Object.keys(obj).forEach(key => {
        if (typeof obj[key] === 'string') {
          obj[key] = obj[key].trim();
        }
      });
      return obj;
    });
    return fixedObjArray;
  }

  processOperations(workbook, country) {
    const allOperationsRowsWith = xlsx.utils.sheet_to_json(workbook.Sheets.Operations, { raw: this.dataReaderConfig.RAW });
    const allOperationsRowsWithBoolenFixed = this.fixStringBoolean(allOperationsRowsWith);
    const allOperationsRowsWithBoolenAndStringFixed = this.fixString(allOperationsRowsWithBoolenFixed);
    const filteredOperations = allOperationsRowsWithBoolenAndStringFixed
      .filter(op => op.skip === 'FALSE')
      .filter(op => op.country.toUpperCase() === country.toUpperCase());
    filteredOperations.forEach(op => {
      if (op.loadTD === 'TRUE') {
        const loadTD = xlsx.utils.sheet_to_json(workbook.Sheets.loadTD, { raw: this.dataReaderConfig.RAW });
        if (loadTD.length > 0) {
          // eslint-disable-next-line no-param-reassign
          op.payments = loadTD.filter(payment => payment.companyOperationId === op.companyOperationId);
          // eslint-disable-next-line no-param-reassign
          op.payments = this.fixStringBoolean(op.payments);
        } else {
          // eslint-disable-next-line no-param-reassign
          op.payments = xlsx.utils.sheet_to_json(workbook.Sheets[op.companyOperationId], { raw: this.dataReaderConfig.RAW });
          // eslint-disable-next-line no-param-reassign
          op.payments = this.fixStringBoolean(op.payments);
        }
      }
    });
    const validatedOperations = filteredOperations.map(op => new StandardOperation(op));
    return validatedOperations;
  }

  async process(file, country) {
    return new Promise((resolve, reject) => {
      let operations = [];
      this.dataReader.onerror = () => {
        this.dataReader.abort();
        reject(new Error('error reading file'));
      };
      this.dataReader.onload = e => {
        const fileData = e.target.result;
        const workbook = xlsx.read(fileData, {
          type: this.dataReaderConfig.TYPE,
          cellText: this.dataReaderConfig.CELLTEXT,
          cellDates: this.dataReaderConfig.CELLDATES,
          dateNF: this.dataReaderConfig.DATENF
        });

        const operationsType = this.getOperationsType(workbook);
        try {
          // Hablamos de MORTGAGE aunque puede ser HOME_EQUITY también en brasil
          if (operationsType === 'MORTGAGE') operations = this.processMortgageOperations(workbook, country);
          else if (operationsType === 'SME') operations = this.processSMEOperations(workbook);
          else if (operationsType === 'OPERATIONS') operations = this.processOperations(workbook, country);
        } catch (error) {
          reject(error);
        }
        resolve(operations);
      };
      this.dataReader.readAsBinaryString(file);
    });
  }
}
